import {
  type QueryKey,
  QueryClient,
  QueryCache,
  type QueryClientConfig,
} from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import keyBy from "lodash/keyBy";
import merge from "lodash/merge";
import values from "lodash/values";

import { ADS_API_VERSIONS, getAdsEndpoint } from "@/api/ads/hooks/useAds";
import appConfig from "@/config/app-config";
import toast from "@/toast";
import {
  handleCloudflareChallenge,
  needsCloudflareChallenge,
} from "@/utils/cf-challenge";
import { flattenPages } from "@/utils/queries";

import { EntityType, QueryKeys } from "../query-keys";

import {
  importAccounts,
  importAccountsFromStatuses,
} from "./importers/account-importer";
import { importFeeds } from "./importers/feed-importer";
import { importGroups } from "./importers/group-importer";
import { importGroupRelationships } from "./importers/group-relationship-importer";
import { importGroupTags } from "./importers/group-tag-importer";
import { importPollsFromStatuses } from "./importers/poll-importer";
import { importPredictionStatuses } from "./importers/prediction-importer";
import { importRelationships } from "./importers/relationship-importer";
import {
  importStatuses,
  importStatusesFromAlerts,
} from "./importers/status-importer";

import type {
  Account,
  Ad,
  Feed,
  Group,
  GroupRelationship,
  GroupTag,
  Alert,
  Relationship,
  Status,
  GroupCategory,
  PredictionStatus,
  PredictionCategory,
} from "@/schemas";

function importEntities<T>(queryKey: QueryKey, entities: T[]) {
  queryClient.setQueryData(queryKey, (prevEntities: T[] | undefined) => {
    if (prevEntities) {
      return values(
        merge({}, keyBy([...prevEntities], "id"), keyBy([...entities], "id")),
      );
    }

    return entities;
  });
}

const isTest = appConfig.NODE_ENV === "test";

let logger: QueryClientConfig["logger"] = undefined;
if (isTest) {
  logger = {
    log: console.log,
    warn: console.warn,
    error: () => {},
  };
}

const queryClient = new QueryClient({
  logger,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: isTest ? 0 : 300000, // 5 minutes
      cacheTime: Infinity,
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onSuccess(data, query) {
      if (query.meta?.entity) {
        switch (query.meta?.entity as EntityType) {
          case EntityType.ACCOUNTS: {
            const accounts = flattenPages(data as any) as Account[];
            importAccounts(accounts);

            break;
          }
          case EntityType.ADS: {
            const ads = flattenPages(data as any) as Ad[];
            const statuses = ads
              .filter((ad) => !!ad.status)
              .map((ad) => ad.status) as Status[];
            importStatuses(statuses);
            importAccountsFromStatuses(statuses);

            break;
          }
          case EntityType.FEEDS: {
            const feeds = flattenPages(data as any) as Feed[];
            importFeeds(feeds);

            break;
          }
          case EntityType.GROUP_CATEGORIES: {
            const groupCategories = flattenPages(
              data as any,
            ) as GroupCategory[];
            importEntities(QueryKeys.groupCategories.global, groupCategories);

            break;
          }
          case EntityType.GROUP_RELATIONSHIPS: {
            const groupRelationships = flattenPages(
              data as any,
            ) as GroupRelationship[];
            importGroupRelationships(groupRelationships);

            break;
          }
          case EntityType.GROUP_TAGS: {
            const groupTags = flattenPages(data as any) as GroupTag[];
            importGroupTags(groupTags);

            break;
          }
          case EntityType.GROUPS: {
            const groups = flattenPages(data as any) as Group[];
            importGroups(groups);

            break;
          }
          case EntityType.ALERTS: {
            const alerts = flattenPages(data as any) as Alert[];
            importStatusesFromAlerts(alerts);
            break;
          }
          case EntityType.RELATIONSHIPS: {
            const relationships = flattenPages(data as any) as Relationship[];
            importRelationships(relationships);

            break;
          }
          case EntityType.STATUSES: {
            const statuses = flattenPages(data as any) as Status[];
            importStatuses(statuses);
            importAccountsFromStatuses(statuses);
            importPollsFromStatuses(statuses);

            break;
          }
          case EntityType.PREDICTIONS: {
            const predictionStatuses = flattenPages(
              data as any,
            ) as PredictionStatus[];
            importPredictionStatuses(predictionStatuses);

            break;
          }
          case EntityType.PREDICTION_CATEGORIES: {
            const predictionCategories = flattenPages(
              data as any,
            ) as PredictionCategory[];
            importEntities(
              QueryKeys.predictionCategories.global,
              predictionCategories,
            );

            break;
          }
          default:
            break;
        }
      }
    },
    onError(error) {
      // Ignore non-axios errors
      if (!(error instanceof AxiosError)) return;

      // Ignore cancelled requests
      const isBlockedFromBrowser =
        ADS_API_VERSIONS.some(
          (version) => error.config?.url === getAdsEndpoint(version),
        ) && !error.response;

      if (axios.isCancel(error) || isBlockedFromBrowser) return;

      // Ignore 404 errors
      if ((error as AxiosError).response?.status === 404) return;

      if (needsCloudflareChallenge(error)) {
        handleCloudflareChallenge();
        return;
      }

      toast.showAlertForError(error);
    },
  }),
});

export { queryClient, importEntities };
